import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  AudienceActiveUniqueCountsDto,
  AudienceWithCountsDto,
  CrmAudiencePlatformSupportDto,
  CrmAudienceUploadDto,
  CrmAudienceWithLatestUploadDto,
  CrmCsvUploadResultDto,
  CrmCsvValidationResultDto,
  DownloadCrmCsvTemplatePlatformEnum,
  GetAllReportsForAudiencePlatformEnum,
  LookerReportOutputDto,
  ReportDataDto,
  UploadCrmCsvForExistingCrmAudiencePlatformEnum,
  UploadCrmCsvForExistingCrmAudienceRefreshModeEnum,
  UploadCrmCsvForValidationPlatformEnum,
  UploadCrmCsvPlatformEnum,
} from '@kortxio/hub-api';
import { parseDownloadFilename } from 'features/ai-responses/helper';
import { RunAudienceQueryParams } from 'features/audiences/types';
import { setError } from 'features/error/slice';
import api from 'libs/api';
import { parseApiPayload, toApiError } from 'libs/api/helper';
import { ApiError } from 'libs/api/types';
import { downloadBlob } from 'libs/media';

export const getAllAudiences = createAsyncThunk<
  AudienceWithCountsDto[],
  {
    clientId: number;
    advertiserId: number;
  }
>(
  'audience/getAllAudiences',
  async ({ clientId, advertiserId }, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.audiences.getAllAudiencesWithCountsByClientIdAndAdvertiserId(
          clientId,
          advertiserId
        );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (apiError.isApi404Error) {
        return [];
      }
      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getAllCrmAudiences = createAsyncThunk<
  CrmAudienceWithLatestUploadDto[],
  {
    clientId: number;
    advertiserId: number;
  }
>(
  'audience/getAllCrmAudiences',
  async ({ clientId, advertiserId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.crmCsv.getAllCrmAudiences(
        clientId,
        advertiserId
      );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (apiError.isApi404Error) {
        return [];
      }
      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getAllCrmAudiencePlatformSupport = createAsyncThunk<
  CrmAudiencePlatformSupportDto[],
  {
    clientId: number;
    advertiserId: number;
  }
>(
  'audience/getAllCrmAudiencePlatformSupport',
  async ({ clientId, advertiserId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.crmCsv.getAllCrmAudiencePlatformSupport(
        clientId,
        advertiserId
      );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const downloadCrmCsvTemplate = createAsyncThunk<
  void,
  {
    clientId: number;
    advertiserId: number;
    platform: DownloadCrmCsvTemplatePlatformEnum;
  }
>(
  'audience/downloadCrmCsvTemplate',
  async (
    { clientId, advertiserId, platform },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await api.crmCsv.downloadCrmCsvTemplate(
        clientId,
        advertiserId,
        platform,
        { responseType: 'blob' }
      );

      const blob = parseApiPayload(response) as unknown as Blob;
      const filename = parseDownloadFilename(
        response,
        `${platform.toLowerCase()}-crm-template.csv`
      );

      await downloadBlob(blob, filename);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export type ValidateCrmCsvArgs = {
  clientId: number;
  advertiserId: number;
  platform: UploadCrmCsvForValidationPlatformEnum;
  file: File;
};

export const validateCrmCsv = createAsyncThunk<
  CrmCsvValidationResultDto,
  ValidateCrmCsvArgs
>(
  'audience/validateCrmCsv',
  async (
    { clientId, advertiserId, platform, file },
    { dispatch, rejectWithValue, signal }
  ) => {
    try {
      const response = await api.crmCsv.uploadCrmCsvForValidation(
        clientId,
        advertiserId,
        platform,
        file,
        { signal }
      );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export type UploadCrmCsvArgs = {
  clientId: number;
  advertiserId: number;
  platform: UploadCrmCsvPlatformEnum;
  file: File;
};

export const uploadCrmCsv = createAsyncThunk<
  CrmCsvUploadResultDto,
  UploadCrmCsvArgs,
  { rejectValue: ApiError }
>(
  'audience/uploadCrmCsv',
  async (
    { clientId, advertiserId, platform, file },
    { rejectWithValue, signal }
  ) => {
    try {
      const response = await api.crmCsv.uploadCrmCsv(
        clientId,
        advertiserId,
        platform,
        file,
        { signal }
      );

      return parseApiPayload(response);
    } catch (error) {
      // CrmCsvUploadFailureAlert shows every failure, so skip setError.
      return rejectWithValue(toApiError(error));
    }
  }
);

export type UploadCrmCsvForExistingCrmAudienceArgs = {
  clientId: number;
  advertiserId: number;
  platform: UploadCrmCsvForExistingCrmAudiencePlatformEnum;
  crmAudienceId: number;
  refreshMode: UploadCrmCsvForExistingCrmAudienceRefreshModeEnum;
  file: File;
};

export const uploadCrmCsvForExistingCrmAudience = createAsyncThunk<
  CrmCsvUploadResultDto,
  UploadCrmCsvForExistingCrmAudienceArgs,
  { rejectValue: ApiError }
>(
  'audience/uploadCrmCsvForExistingCrmAudience',
  async (
    { clientId, advertiserId, platform, crmAudienceId, refreshMode, file },
    { rejectWithValue, signal }
  ) => {
    try {
      const response = await api.crmCsv.uploadCrmCsvForExistingCrmAudience(
        clientId,
        advertiserId,
        platform,
        crmAudienceId,
        file,
        refreshMode,
        { signal }
      );

      return parseApiPayload(response);
    } catch (error) {
      // CrmCsvUploadForExistingCrmAudienceFailureAlert shows every failure.
      return rejectWithValue(toApiError(error));
    }
  }
);

export const getSelectedCrmAudience = createAsyncThunk<
  CrmAudienceWithLatestUploadDto,
  {
    clientId: number;
    advertiserId: number;
    crmAudienceId: number;
    isRefresh?: boolean;
  }
>(
  'audience/getSelectedCrmAudience',
  async (
    { clientId, advertiserId, crmAudienceId, isRefresh },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await api.crmCsv.getCrmAudience(
        clientId,
        advertiserId,
        crmAudienceId
      );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError && !isRefresh) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getAllCrmAudienceUploads = createAsyncThunk<
  CrmAudienceUploadDto[],
  {
    clientId: number;
    advertiserId: number;
    crmAudienceId: number;
    isRefresh?: boolean;
  }
>(
  'audience/getAllCrmAudienceUploads',
  async (
    { clientId, advertiserId, crmAudienceId, isRefresh },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await api.crmCsv.getAllCrmAudienceUploads(
        clientId,
        advertiserId,
        crmAudienceId
      );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError && !isRefresh) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getAudienceActiveUniqueCountsById = createAsyncThunk<
  AudienceActiveUniqueCountsDto,
  {
    clientId: number;
    advertiserId: number;
    id: string;
  }
>(
  'audience/getAudienceActiveUniqueCountsById',
  async ({ clientId, advertiserId, id }, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.audiences.getAudienceActiveUniqueCountsByClientIdAndAdvertiserIdAndId(
          clientId,
          advertiserId,
          id
        );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (apiError.isApi404Error) {
        return [];
      }
      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getAllReportsForAudience = createAsyncThunk<
  LookerReportOutputDto[],
  {
    clientId: number;
    advertiserId: number;
    audienceId: string;
    platform: GetAllReportsForAudiencePlatformEnum;
  }
>(
  'audienceReports/getAllReportsForAudience',
  async (
    { clientId, advertiserId, audienceId, platform },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await api.audienceReports.getAllReportsForAudience(
        clientId,
        advertiserId,
        audienceId,
        platform
      );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const runQueryForAudience = createAsyncThunk<
  ReportDataDto,
  RunAudienceQueryParams
>(
  'audienceReports/runQueryForAudience',
  async (
    { clientId, advertiserId, audienceId, id, queryId, platform },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await api.audienceReports.postRunQueryForAudience(
        clientId,
        advertiserId,
        audienceId,
        id,
        queryId,
        platform
      );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);
